import { registerActions } from "../actionRegistry";
/**
 * Interior Selection Functions
 *
 * Functions for selecting and managing interior images/mockups.
 */

import { api } from "../api";
import { toastSuccess, toastError } from "../ui/messages";
import { closeModalLast, closeModalLastRefresh } from "../ui/modal";
import { getDomElement, getDomElementOrThrow, setHtml, queryAll, idSuffix, h } from "../utils/dom";

/** Raw ids of the `.interior-select` entries whose hidden input is switched on. */
function selectedMockupIds(): string[] {
  return Array.from(queryAll<HTMLElement>(".interior-select"), (el) => idSuffix(el, "interior-"))
    .filter((raw_id) => raw_id !== "")
    .filter((raw_id) => getDomElement<HTMLInputElement>(`interior-${raw_id}`)?.value === "1");
}

/**
 * Paint every `div.selectinterior_<aspect>` slot with `html` and store `value` in
 * its paired `shopifysync-<id>-interior` hidden input.
 */
function applyToAspectSlots(aspect: "s" | "p" | "l", html: string, value: string): void {
  for (const el of queryAll<HTMLElement>(`div.selectinterior_${aspect}`)) {
    const raw_id = idSuffix(el, "selectinterior-");
    if (!raw_id) continue;
    el.innerHTML = html;
    getDomElementOrThrow<HTMLInputElement>(`shopifysync-${raw_id}-interior`).value = value;
  }
}

/** The preview <img> markup the selection slots use. */
function slotImageHtml(src: string): string {
  return `<img src="${src}" style="margin: 10px 0px 10px 0px; max-width: 150px; max-height: 80px; border-radius: 5px;">`;
}

/**
 * Select an interior by ID and apply to the current selection target
 */
export function selectMockupById(interior_id: string): void {
  const select_id = getDomElement<HTMLInputElement>("selectinterior_id")?.value;

  if (parseInt(select_id ?? "") > 0) {
    const img_id = getDomElementOrThrow<HTMLImageElement>(`interiorsrc-${interior_id}`).src;
    setHtml(`selectinterior-${select_id}`, slotImageHtml(img_id));
    getDomElementOrThrow<HTMLInputElement>(`shopifysync-${select_id}-interior`).value = interior_id;
    closeModalLast();
  }
}

/**
 * Apply new interior selection for all aspects
 */
export function selectMockupNew(): void {
  const byAspect: Record<"s" | "p" | "l", string[]> = { s: [], p: [], l: [] };

  for (const raw_id of selectedMockupIds()) {
    const aspect = getDomElement<HTMLInputElement>(`interior-asp-${raw_id}`)?.value;
    if (aspect === "p" || aspect === "l" || aspect === "s") {
      byAspect[aspect].push(raw_id);
    }
  }

  for (const aspect of ["s", "p", "l"] as const) {
    const ids = byAspect[aspect];
    const plural = ids.length !== 1 ? "s" : "";
    applyToAspectSlots(
      aspect,
      `${ids.length} interior${plural} selected &nbsp; &nbsp; <span class="button small black">Change</span>`,
      ids.join(",")
    );
  }

  toastSuccess("Interior selection has been applied");
  closeModalLast();
}

/**
 * Apply interior selection from radio buttons
 */
export function saveMockupSelection(): void {
  const select_id = getDomElement<HTMLInputElement>("selectinterior_id")?.value;

  if (select_id === "all") {
    for (const aspect of ["s", "p", "l"] as const) {
      const checked = document.querySelector<HTMLInputElement>(
        `input[name="set-${aspect}"]:checked`
      );
      if (checked === null) continue;
      const src = getDomElementOrThrow<HTMLImageElement>(`interiorsrc-${checked.value}`).src;
      applyToAspectSlots(aspect, slotImageHtml(src), checked.value);
    }

    closeModalLast();
  }
}

export async function saveDefaultMockups(): Promise<void> {
  // One flat id list — orientation derives server-side from Mockup.aspect.
  const ids = selectedMockupIds();

  try {
    await api.mockups.saveDefaults({ ids });
    toastSuccess("Default interiors saved");
    closeModalLastRefresh();
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function toggleMockupSel(id: string, aspect: string): Promise<void> {
  try {
    const response = await api.mockups.toggleSelection({ id });
    const on = response.state === "on";
    toastSuccess(on ? "Interior added to your selection" : "Interior removed from your selection");
    // Built as a node so the button IS the reference the listener binds to.
    // Previously this wrote the markup with `setHtml`, then re-found the
    // container by id and `querySelector("span")`-ed its way back to the element
    // it had just created.
    const container = getDomElement(`addcontainer-${id}`);
    if (container) {
      const button = on
        ? h("span", "button medium red", h("i", "fas fa-minus"), " Remove")
        : h("span", "button medium green", h("i", "fas fa-plus"), " Add");
      button.addEventListener("click", () => void toggleMockupSel(id, aspect));
      container.replaceChildren(button);
    }
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

registerActions({
  selectinteriorid: selectMockupById,
  selectinteriornew: selectMockupNew,
  selectinterior: saveMockupSelection,
  savedefaultinteriors: saveDefaultMockups,
  toggleInteriorSel: toggleMockupSel,
});
